import type { GameState, Pile } from '../../engine/game-state.js';
import { createLayout, rowAbove } from '../../render/three/layout.js';
import type { GameSpec } from '../../render/three/game-app.js';
import { acesupRules, dealRow, DISCARD_ID, TABLEAU_IDS } from './rules.js';

const TABLEAU_Z = 0.0;
const TOP_Z = rowAbove(TABLEAU_Z);

// 4 columns. Stock sits above the leftmost pile, the discard above the
// rightmost, with the four piles fanning down between them.
const layout = createLayout({
  cols: 4,
  rows: [
    { z: TOP_Z, slots: ['stock', null, null, DISCARD_ID] },
    { z: TABLEAU_Z, fanDown: true, slots: [...TABLEAU_IDS] },
  ],
});

function dragStackIds(state: GameState, cardId: string): string[] {
  for (const pile of Object.values(state.piles)) {
    const idx = pile.cards.findIndex((c) => c.id === cardId);
    if (idx === -1) continue;
    if (pile.type !== 'tableau') return [];
    if (idx !== pile.cards.length - 1) return []; // only the top card moves
    return [cardId];
  }
  return [];
}

function discard(state: GameState, pile: Pile, cardId: string): boolean {
  const move = { from: pile.id, to: DISCARD_ID, cardId };
  if (!acesupRules.canMove(state, move)) return false;
  const card = pile.cards.pop()!;
  state.piles[DISCARD_ID]!.cards.push(card);
  state.movesCount++;
  return true;
}

export const acesupSpec: GameSpec = {
  rules: acesupRules,
  slotForPile: (id) => layout.slot(id),
  // Dragging targets the discard or an empty pile; never the stock.
  dropExclude: () => ['stock'],
  dragStackIds,
  autoSendOrder: () => [DISCARD_ID],
  // A pile that's never discarded from receives every deal: 1 + 12 = 13.
  maxFanLength: 13,
  onPileClick(state, pile, cardId) {
    if (pile.id === 'stock') {
      if (!dealRow(state)) return false;
      state.movesCount++;
      return true;
    }
    if (pile.type === 'tableau') {
      return discard(state, pile, cardId);
    }
    return false;
  },
  statusText(state) {
    const dealsLeft = Math.floor(state.piles.stock!.cards.length / TABLEAU_IDS.length);
    if (acesupRules.isLost!(state)) return 'No moves left';
    if (dealsLeft > 0) return `${dealsLeft} deal${dealsLeft === 1 ? '' : 's'} left`;
    return null;
  },
  cascadeCardIds(state) {
    return state.piles[DISCARD_ID]!.cards.map((c) => c.id);
  },
};
